import { useState, type ReactElement } from 'react';
import { Button, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { EntityRelatedContext } from '../../../components/entity-detail/related-context.js';
import { formatDateTime, formatMoney } from '../../../shared/format/entity-detail.js';
import { useDocuments } from '../../documents/hooks/useDocuments.js';
import { useDeleteDocumentMutation } from '../../documents/hooks/useDocumentMutations.js';
import {
  DocumentFormModal,
  DOCUMENT_TYPE_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
} from '../../documents/components/DocumentFormModal.js';

export function ContactDocumentsTab({ contactId }: { contactId: string }): ReactElement {
  return <EntityDocumentsTab contactId={contactId} />;
}

export function EntityDocumentsTab({
  contactId,
  dealId,
  companyId,
  showAmount = false,
}: EntityRelatedContext & {
  /** Сумма документа в строке — карточка компании показывает её вместо старой колонки. */
  showAmount?: boolean;
}): ReactElement {
  const { t, locale } = useTranslation();
  const query = useDocuments(
    dealId ? { dealId } : companyId ? { companyId } : { contactId: contactId! },
  );
  const remove = useDeleteDocumentMutation();
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState('original');
  const [direction, setDirection] = useState<'ascend' | 'descend'>('ascend');
  const documents = [...query.documents].sort((a, b) => {
    if (sort === 'original') return 0;
    const order =
      (sort === 'created'
        ? Date.parse(a.createdAt) - Date.parse(b.createdAt)
        : a.title.localeCompare(b.title, locale)) || a.title.localeCompare(b.title, locale);
    return direction === 'ascend' ? order : -order;
  });
  return (
    <>
      <EntityRelatedList
        loading={query.isLoading}
        error={query.error}
        emptyText={t('crm.entity_detail.empty_documents')}
        filter={
          documents.length ? (
            <div className="contact-deals-filter company-detail-related-sort">
              <Select
                size="small"
                aria-label={t('crm.entity_detail.sort')}
                value={sort}
                onChange={setSort}
                style={{ minWidth: 160 }}
                options={[
                  { value: 'original', label: t('crm.features.company_detail.sort_original') },
                  { value: 'title', label: t('crm.entity_detail.sort_title') },
                  { value: 'created', label: t('crm.features.company_detail.sort_created') },
                ]}
              />
              <Select
                size="small"
                aria-label={t('crm.features.company_detail.sort_direction')}
                value={direction}
                onChange={setDirection}
                disabled={sort === 'original'}
                style={{ minWidth: 160 }}
                options={['ascend', 'descend'].map((value) => ({
                  value,
                  label: t(`crm.features.company_detail.sort_${value}`),
                }))}
              />
            </div>
          ) : null
        }
        action={
          <Button size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            {t('crm.entity_detail.add_document')}
          </Button>
        }
        rows={documents.map((document) => ({
          id: document.id,
          title: document.title,
          href: `/documents/${document.id}`,
          status: (
            <EntityStatusChip tone={document.status === 'signed' ? 'success' : 'default'}>
              {DOCUMENT_STATUS_OPTIONS.find((o) => o.value === document.status)?.label ??
                document.status}
            </EntityStatusChip>
          ),
          meta: (
            <>
              {DOCUMENT_TYPE_OPTIONS.find((o) => o.value === document.type)?.label ?? document.type}
              {showAmount && document.amount !== null && document.amount !== undefined ? (
                <>
                  {' · '}
                  <span style={MONO_NUMERIC_STYLE}>
                    {formatMoney(document.amount, document.currency)}
                  </span>
                </>
              ) : null}
            </>
          ),
          value: formatDateTime(document.createdAt),
          actions: [
            {
              key: 'delete',
              label: t('crm.entity_detail.delete'),
              danger: true,
              disabled: remove.isPending,
              confirm: { title: t('crm.entity_detail.delete_confirm') },
              onClick: () => remove.mutateAsync(document.id),
            },
          ],
        }))}
      />
      {open ? (
        <DocumentFormModal
          open
          {...(dealId
            ? { lockedDealId: dealId }
            : companyId
              ? { lockedCompanyId: companyId }
              : { lockedContactId: contactId! })}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
import { MONO_NUMERIC_STYLE } from '../../../shared/ui/list-styles.js';
